import React, { FC, useEffect, useState } from 'react';
import {
  IonContent,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonRange,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { Text } from '../sketch-tool';

interface TextEditProps {
  object: Text;
  isOpen: boolean;
  onClose: (event: any) => any;
}

const TextEdit: FC<TextEditProps> = ({ object, isOpen, onClose }: TextEditProps) => {
  const [text, setText] = useState('');
  const [fontSize, setFontSize] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen || !object) return;
    setText(object.text);
    setFontSize(object.fontSize);
    setRotation(object.rotation);
    setWidth(object.width);
    setHeight(object.height);
  }, [isOpen, object]);

  useEffect(() => {
    if (!isOpen || !object) return;
    object.setText(text);
  }, [text, object, isOpen]);

  useEffect(() => {
    if (!isOpen || !object) return;
    object.setFontSize(fontSize);
  }, [fontSize, object, isOpen]);

  useEffect(() => {
    if (!isOpen || !object) return;
    object.setRotation(rotation);
  }, [rotation, object, isOpen]);

  useEffect(() => {
    if (!isOpen || !object) return;
    object.setWidth(width);
  }, [width, object, isOpen]);

  useEffect(() => {
    if (!isOpen || !object) return;
    object.setHeight(height);
  }, [height, object, isOpen]);

  const handleTextChange = (event: any) => {
    setText(event.detail.value);
  };

  const handleFontSizeChange = (event: any) => {
    setFontSize(Number(event.detail.value));
  };

  const handleRotationChange = (event: any) => {
    setRotation(Number(event.detail.value));
  };

  const handleWidthChange = (event: any) => {
    setWidth(Number(event.detail.value));
  };

  const handleHeightChange = (event: any) => {
    setHeight(Number(event.detail.value));
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} initialBreakpoint={0.75} breakpoints={[0, 0.25, 0.5, 0.75, 1]}>
      <IonContent className="ion-padding">
        <div style={{ overflow: 'scroll' }}>
          <IonList>
            <IonListHeader>
              <IonLabel>
                {t('content').toString()}
              </IonLabel>
            </IonListHeader>
            <IonItem>
              <IonLabel position="stacked">
                {t('text').toString()}
              </IonLabel>
              <IonInput value={text} onIonChange={handleTextChange} type="text" />
            </IonItem>
            <IonListHeader>
              <IonLabel>
                {t('dimensions').toString()}
              </IonLabel>
            </IonListHeader>
            <IonItem>
              <IonLabel position="stacked">
                {t('fontSize').toString()}
              </IonLabel>
              <IonInput value={fontSize} onIonChange={handleFontSizeChange} type="number" />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">
                {t('width').toString()}
              </IonLabel>
              <IonInput value={width} onIonChange={handleWidthChange} type="number" />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">
                {t('height').toString()}
              </IonLabel>
              <IonInput value={height} onIonChange={handleHeightChange} type="number" />
            </IonItem>
            <IonListHeader>
              <IonLabel>
                {t('position').toString()}
              </IonLabel>
            </IonListHeader>
            <IonItem lines="none">
              <IonLabel position="stacked">
                {t('rotation').toString()}
              </IonLabel>
              <IonRange
                value={rotation}
                onIonChange={handleRotationChange}
                pin
                pinFormatter={(value: number) => `${value}˚`}
                max={360}
              />
            </IonItem>
          </IonList>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default TextEdit;